import React, { useState } from 'react'
import { useMutation } from "@apollo/client/react";
import { Button } from '@/atoms/Button'
import { TitleXXS } from '@/atoms/Text'
import { UNITED_STATES } from '@/constants/regions'
import { VALIDATE_AND_SAVE_SHIPPING_ADDRESS, VALIDATE_AND_SAVE_USER_ADDRESS } from '@/services/NftService'
import { Address } from '@/services/NftService/types'
import {
  Validate_And_Save_Shipping_AddressMutation,
  Validate_And_Save_Shipping_AddressMutationVariables,
  Validate_And_Save_User_AddressMutation,
} from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import StateOptions from './StateOptions'

interface AddressChangeProps {
  address?: Address
  shippingAddress?: Address
  isAddressChange?: boolean
  hideContinueButton?: boolean
  isShippingAddress?: boolean
  cancelAddressChange: () => void
  successAddressChange?: () => void
  successShippingAddressChange?: () => void
  orderId?: string
  setShippingAddress?: (address: Address) => void
}

const AddressChange: React.FC<AddressChangeProps> = ({
  address,
  cancelAddressChange,
  successAddressChange,
  successShippingAddressChange,
  hideContinueButton = false,
  isShippingAddress = false,
  orderId,
  shippingAddress,
  setShippingAddress,
}) => {
  const [line1, setLine1] = useState(address?.line1 || '')
  const [line2, setLine2] = useState(address?.line2 || '')
  const [city, setCity] = useState(address?.city || '')
  const [state, setState] = useState(address?.state || '')
  const [zip, setZip] = useState(address?.zip || '')

  const { t } = useTranslate('account')

  const [errorMessage, setErrorMessage] = useState('')

  const [validateAndSaveUserAddress] = useMutation<Validate_And_Save_User_AddressMutation>(VALIDATE_AND_SAVE_USER_ADDRESS, {
    onCompleted: (data) => {
      if (data?.validateAndSaveUserAddress?.success) {
        successAddressChange?.()
      }
    },
    onError: (error) => {
      setErrorMessage(error.message)
    },
    errorPolicy: 'all',
  })

  const [validateAndSaveShippingAddress] = useMutation<
    Validate_And_Save_Shipping_AddressMutation,
    Validate_And_Save_Shipping_AddressMutationVariables
  >(VALIDATE_AND_SAVE_SHIPPING_ADDRESS, {
    onCompleted: (data) => {
      if (data?.updateShippingAddress?.success) {
        successShippingAddressChange?.()
      }
    },
    onError: (error) => {
      setErrorMessage(error.message)
    },
    errorPolicy: 'all',
  })

  const handleSave = () => {
    if (isShippingAddress) {
      validateAndSaveShippingAddress({
        variables: {
          input: {
            address: {
              line1,
              line2,
              city,
              state,
              zip,
              country: UNITED_STATES,
            },
            orderId: orderId ?? '',
          },
        },
      })
      setShippingAddress?.({
        line1,
        line2,
        city,
        state,
        zip,
        country: UNITED_STATES,
      })
    } else {
      validateAndSaveUserAddress({
        variables: {
          input: {
            line1,
            line2,
            city,
            state,
            zip,
            country: UNITED_STATES,
          },
        },
      })
    }
  }

  const handleCancel = () => {
    cancelAddressChange()
  }

  const resetErrorMessage = () => {
    if (errorMessage) {
      setErrorMessage('')
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-col w-full text-sm gap-y-2">
        <div className="flex flex-col">
          <TitleXXS className="mb-1.5">{t('addressLine1', 'Address 1')}</TitleXXS>
          <input
            name="firstLine"
            type="text"
            className="p-2 rounded-md border-[1px] border-core-gray-200 text-black"
            value={line1}
            onChange={(e) => {
              resetErrorMessage()
              setLine1(e.target.value)
            }}
          />
        </div>
        <div className="flex flex-col">
          <TitleXXS className="mb-1.5">{t('addressLine2', 'Address 2')}</TitleXXS>
          <input
            name="secondLine"
            type="text"
            className="p-2 rounded-md border-[1px] border-core-gray-200 text-black"
            value={line2}
            onChange={(e) => {
              resetErrorMessage()
              setLine2(e.target.value)
            }}
          />
        </div>
        <div className="flex flex-col lg:flex-row gap-2 lg:gap-y-0">
          <div className="flex flex-col flex-1">
            <TitleXXS className="mb-1.5">{t('city', 'City')}</TitleXXS>
            <input
              name="city"
              type="text"
              className="p-2 rounded-md border-[1px] border-core-gray-200 text-black"
              value={city}
              onChange={(e) => {
                resetErrorMessage()
                setCity(e.target.value)
              }}
            />
          </div>
          <div className="flex flex-col flex-1">
            <TitleXXS className="mb-1.5">{t('state', 'State')}</TitleXXS>
            <select
              name="state"
              className="p-2 rounded-md border-[1px] border-core-gray-200 text-black"
              value={state.toUpperCase()}
              onChange={(e) => {
                resetErrorMessage()
                setState(e.target.value)
              }}
            >
              <StateOptions />
            </select>
          </div>
        </div>
        <div className="flex flex-col">
          <TitleXXS className="mb-1.5">{t('addressZip', 'Zip Code')}</TitleXXS>
          <input
            name="zip"
            type="text"
            className="p-2 rounded-md border-[1px] border-core-gray-200 text-black"
            value={zip}
            onChange={(e) => {
              resetErrorMessage()
              setZip(e.target.value)
            }}
          />
        </div>
      </div>
      {errorMessage && <div className="text-center text-sm text-red">{errorMessage}</div>}
      {!hideContinueButton && (
        <Button className="w-full sm:w-fit mt-4" disabled={!!errorMessage} onClick={handleSave} outline variant="black">
          <TitleXXS weight="bold">{t('continue', 'Continue')}</TitleXXS>
        </Button>
      )}
    </div>
  )
}

export default AddressChange
